import api from "../lib/api";
import CompagniaField from "./CompagniaField";
import DateInput from "./DateInput";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import Combobox, { COLORI_VEICOLO } from "./Combobox";
import AutocompleteInput from "./AutocompleteInput";

const TIPI_VEICOLO = ["Autovettura", "Motociclo", "Ciclomotore", "Autocarro", "Autobus", "Rimorchio", "Altro"];
const normTarga = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const LBL = "text-[11px] uppercase tracking-wider text-slate-400 font-mono";
const INP = "mt-1.5 h-9 bg-slate-900/60 border-slate-700 text-slate-100";

// Sezione veicolo per la Controparte (wizard Step 3 + scheda anagrafica).
// value: oggetto veicolo | null. onChange(veicolo|null).
export default function ControparteVeicolo({ veicolo, onChange, testId = "cp-veicolo" }) {
  const v = veicolo;
  const set = (k, val) => onChange({ ...(v || {}), [k]: val });

  const add = () => onChange({
    targa: "", tipo_veicolo: "", marca: "", modello: "", anno_immatricolazione: "", colore: "", telaio: "",
    compagnia: "", numero_polizza: "", data_inizio_polizza: "", scadenza_polizza: "", massimale: "", agenzia_broker: "", num_sinistro_compagnia: "",
  });
  const remove = () => onChange(null);
  const ac = (k, tid, cls = "", upper = false) => (
    <AutocompleteInput value={v[k] || ""} onChange={(val) => set(k, upper ? String(val).toUpperCase() : val)} source="cpveicolo"
      extraParams={{ field: k }} testId={`${testId}-${tid}`} className={`h-9 text-slate-100 ${cls}`} />
  );

  const lookupTarga = async () => {
    const t = normTarga(v?.targa);
    if (!t) { toast.info("Inserisci una targa da cercare"); return; }
    try {
      const { data } = await api.get(`/studio/targa-check?targa=${encodeURIComponent(v.targa)}`);
      const rec = data?.match?.record;
      if (rec) {
        const vv = (rec.veicoli || []).find((x) => normTarga(x.targa) === t) || {};
        onChange({
          ...v,
          marca: v.marca || vv.marca || rec.marca || "",
          modello: v.modello || vv.modello || rec.modello || "",
          tipo_veicolo: v.tipo_veicolo || vv.tipo_veicolo || "",
          anno_immatricolazione: v.anno_immatricolazione || vv.anno_immatricolazione || "",
          colore: v.colore || vv.colore || "",
          telaio: v.telaio || vv.telaio || "",
          compagnia: v.compagnia || vv.compagnia || rec.compagnia || "",
          numero_polizza: v.numero_polizza || vv.numero_polizza || rec.numero_polizza || "",
          scadenza_polizza: v.scadenza_polizza || vv.scadenza_polizza || "",
        });
        toast.success(`Targa già presente (${data.match.display}). Dati precompilati.`);
      } else {
        toast.info("Nessun veicolo trovato con questa targa");
      }
    } catch { toast.error("Errore nella ricerca targa"); }
  };

  return (
    <div className="rounded-md border border-slate-700 p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-mono mb-3">Veicolo Controparte</div>
      {!v ? (
        <Button type="button" onClick={add} data-testid={`${testId}-add`} className="border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi Veicolo
        </Button>
      ) : (
        <div className="space-y-4" data-testid={`${testId}-form`}>
          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-mono">Dati Veicolo</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className={LBL}>Targa</Label>
              <div className="flex gap-2 mt-1.5">
                <div className="flex-1">{ac("targa", "targa", "font-mono", true)}</div>
                <Button type="button" onClick={lookupTarga} data-testid={`${testId}-lookup`} title="Cerca targa in anagrafica" className="bg-slate-700 hover:bg-slate-600 text-slate-100 shrink-0"><Search className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label className={LBL}>Tipo Veicolo</Label>
              <Select value={v.tipo_veicolo || undefined} onValueChange={(val) => set("tipo_veicolo", val)}>
                <SelectTrigger data-testid={`${testId}-tipo`} className={`${INP} pgp-select-value`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                  {TIPI_VEICOLO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className={LBL}>Marca</Label><div className="mt-1.5">{ac("marca", "marca")}</div></div>
            <div><Label className={LBL}>Modello</Label><div className="mt-1.5">{ac("modello", "modello")}</div></div>
            <div><Label className={LBL}>Anno Immatricolazione</Label><Input value={v.anno_immatricolazione || ""} onChange={(e) => set("anno_immatricolazione", e.target.value)} data-testid={`${testId}-anno`} className={`${INP} font-mono`} /></div>
            <div><Label className={LBL}>Colore</Label><div className="mt-1.5"><Combobox value={v.colore} onChange={(val) => set("colore", val)} options={COLORI_VEICOLO} placeholder="Scegli o scrivi un colore" testId={`${testId}-colore`} className="text-slate-100" /></div></div>
            <div className="sm:col-span-3"><Label className={LBL}>Telaio (VIN)</Label><div className="mt-1.5">{ac("telaio", "telaio", "font-mono", true)}</div></div>
          </div>

          <div className="text-[11px] uppercase tracking-wider text-amber-300 font-mono pt-1">Dati Assicurativi</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className={LBL}>Compagnia Assicurazione</Label>
              <div className="mt-1.5"><CompagniaField value={v.compagnia} onChange={(val) => set("compagnia", val)} testId={`${testId}-compagnia`} /></div>
            </div>
            <div><Label className={LBL}>N. Polizza</Label><div className="mt-1.5">{ac("numero_polizza", "polizza", "font-mono")}</div></div>
            <div><Label className={LBL}>Massimale (€)</Label><div className="mt-1.5">{ac("massimale", "massimale", "font-mono")}</div></div>
            <div><Label className={LBL}>Data Inizio Polizza</Label><div className="mt-1.5"><DateInput value={v.data_inizio_polizza} onChange={(val) => set("data_inizio_polizza", val)} testId={`${testId}-inizio`} className="h-9 bg-slate-900/60 border-slate-700" /></div></div>
            <div><Label className={LBL}>Data Scadenza Polizza</Label><div className="mt-1.5"><DateInput value={v.scadenza_polizza} onChange={(val) => set("scadenza_polizza", val)} testId={`${testId}-scadenza`} className="h-9 bg-slate-900/60 border-slate-700" /></div></div>
            <div><Label className={LBL}>Agenzia / Broker</Label><div className="mt-1.5">{ac("agenzia_broker", "agenzia")}</div></div>
            <div className="sm:col-span-3"><Label className={LBL}>N. Sinistro Compagnia</Label><div className="mt-1.5">{ac("num_sinistro_compagnia", "num-sinistro", "font-mono")}</div></div>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={remove} data-testid={`${testId}-remove`} className="border border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><Trash2 className="h-4 w-4 mr-1" /> Rimuovi veicolo</Button>
          </div>
        </div>
      )}
    </div>
  );
}
